import type { WhatsAppMessage } from '../types';

/**
 * The conversation's messages, oldest first — the order a conversation reads.
 *
 * Two orders run opposite ways here, which is what makes this easy to get
 * wrong. The API queries newest-first, because cursor pagination needs page one
 * to be the newest batch — and then reverses before returning, so **the rows
 * inside a page arrive oldest-first**. Page order stays newest-batch-first.
 *
 * So the pages are walked backwards and the rows forwards. Reversing both, as a
 * first attempt at this did, puts the newest message back on top and reproduces
 * the bug exactly.
 *
 * That assumption is the reason this is a named function with tests rather than
 * a `.reverse()` inline: getting it wrong is invisible in code review and only
 * shows up when someone reads a conversation backwards.
 */
export function toChronological(
  pages: { items: WhatsAppMessage[] }[] | undefined,
): WhatsAppMessage[] {
  if (!pages?.length) return [];

  const seen = new Set<string>();
  const chronological: WhatsAppMessage[] = [];

  // Oldest page first…
  for (let p = pages.length - 1; p >= 0; p--) {
    // …and its rows already read forward.
    for (const message of pages[p]?.items ?? []) {
      // A cursor boundary can repeat a row, and the list keys on id — a
      // duplicate key crashes the render rather than showing twice.
      if (!message || seen.has(message.id)) continue;

      seen.add(message.id);
      chronological.push(message);
    }
  }

  return chronological;
}
